import { clusters, heroOct, inputsOct } from './teaser'

export interface StoryboardFrame {
  id: string
  index: string
  chapter: string
  onScreen: string
  detail: string
  hcp: string
  repSays: string
  purpose: string
  seconds: string
  image?: string
  images?: string[]
}

export const storyboardMeta = {
  eyebrow: 'Internal · not for HCP use',
  title: 'Storyboard',
  subtitle:
    'Rep-guided iPad presentation · seminars, congresses, HCP visits, education · 9 frames + 2 optional deep dives',
  runtime:
    'Core experience: 60–90 seconds. Deep dives on HCP request only — the same flow expands for seminar and educational use.',
}

export const storyboardFrames: StoryboardFrame[] = [
  {
    id: 'permission',
    index: '01',
    chapter: 'Permission',
    onScreen: 'Can I get your take on one OCT question?',
    detail: '“30 seconds. Your view first, then the data.” Single CTA: Start.',
    hcp: 'Agrees to engage.',
    repSays: '“I’m interested in how you see this — then I’ll show you why the question came up.”',
    purpose: 'Earn permission without presenting anything.',
    seconds: '0–10s',
    image: heroOct.src,
  },
  {
    id: 'question',
    index: '02',
    chapter: 'Your view',
    onScreen: 'When two DME patients look different on OCT, what matters most to you?',
    detail: 'Single select cue: Fluid pattern · Retinal structure · The overall combination.',
    hcp: 'Answers first. No right/wrong feedback shown.',
    repSays: '“What makes you say that?”',
    purpose: 'The ophthalmologist speaks before any data appears.',
    seconds: '10–25s',
  },
  {
    id: 'bridge',
    index: '03',
    chapter: 'The analysis',
    onScreen: 'That variation is exactly what a recent analysis explored.',
    detail:
      '2,355 DME eyes · IRF · SRF · I-HRF · ELM · EZ. Closes with: do recurring morphological patterns emerge?',
    hcp: 'Listens; may recognise the study.',
    repSays: '“Have you seen this study before?”',
    purpose: 'Bridge their answer to the dataset. No variable definitions.',
    seconds: '25–35s',
    image: inputsOct.src,
  },
  {
    id: 'reveal',
    index: '04',
    chapter: 'What emerged',
    onScreen: 'Four distinct morphological clusters emerged.',
    detail: 'Four cluster scans, full-bleed. Any scan can be tapped to enlarge.',
    hcp: 'Interprets the scans before any explanation.',
    repSays: '“Do these look like patterns you recognise from your own patients?”',
    purpose: 'Reveal without teaching. Let them read the imagery.',
    seconds: '35–50s',
    images: clusters.map((c) => c.image),
  },
  {
    id: 'reflection',
    index: '05',
    chapter: 'Your read',
    onScreen: 'Do these patterns resemble the heterogeneity you see in practice?',
    detail:
      'Single select cue: Often · Sometimes · Not really. Live NOK delegate share shown after answering, kept secondary.',
    hcp: 'Places their own practice against the clusters.',
    repSays: '“Which of the patterns feels most familiar to you?”',
    purpose: 'Convert observation into their own experience.',
    seconds: '50–65s',
  },
  {
    id: 'evidence',
    index: '06',
    chapter: 'Credibility',
    onScreen: 'What this does — and does not — tell us',
    detail:
      'Shows: familiar variables formed recurring clusters. Does not show: clusters are not validated treatment-selection categories. Study facts + methodology link.',
    hcp: 'May challenge the strength of the evidence.',
    repSays:
      '“Exactly — it’s not a treatment algorithm. The interesting part is whether familiar morphology can be described more systematically.”',
    purpose: 'Pre-empt scepticism by naming the limits first.',
    seconds: '65–75s',
  },
  {
    id: 'relevance',
    index: '07',
    chapter: 'Relevance',
    onScreen: 'The AI helped identify the pattern. The features are already in the OCT.',
    detail:
      'Routine OCT with the five variables tagged, then the cue: Potentially · Already useful · Not yet convinced.',
    hcp: 'States whether a systematic view could be useful.',
    repSays: '“What would it need to do to become useful for you?”',
    purpose: 'The key discussion point. Capture their conditions for usefulness.',
    seconds: '75–90s',
    image: heroOct.src,
  },
  {
    id: 'depth',
    index: '08',
    chapter: 'Your choice',
    onScreen: 'Where would you like to go next?',
    detail: 'Compare the 4 clusters · Study design · That’s enough for now.',
    hcp: 'Controls the depth of the conversation.',
    repSays: '“How deep would you like to go?”',
    purpose: 'Hand over control; never push further than invited.',
    seconds: 'HCP-led',
  },
  {
    id: 'end',
    index: '09',
    chapter: 'Close',
    onScreen: 'One diagnosis. Different morphological patterns.',
    detail:
      'Closes on the open question of whether recognising patterns more systematically adds useful context. “Thank you for your perspective.”',
    hcp: 'Leaves with a question, not a claim.',
    repSays: '“Thank you — your read on this is the useful part.”',
    purpose: 'No product CTA, no treatment CTA, no lead capture.',
    seconds: 'Close',
  },
]

export const storyboardDeepDives: StoryboardFrame[] = [
  {
    id: 'compare',
    index: 'D1',
    chapter: 'Optional · Compare the 4 clusters',
    onScreen: 'Compare the four morphological clusters',
    detail:
      'Cluster 1: mainly central IRF · no SRF · ELM/EZ intact · fewer I-HRF. Cluster 2: mainly peripheral IRF · no SRF · ELM/EZ intact · more I-HRF. Cluster 3: central + peripheral IRF · SRF present · EZ disruption · higher I-HRF. Cluster 4: central + peripheral IRF · greater SRF · ELM + EZ disruption · higher I-HRF.',
    hcp: 'Compares morphology side by side.',
    repSays: '“Is there anything here you would interpret differently?”',
    purpose: 'Morphological descriptors only — no progression, prognosis or treatment choice.',
    seconds: 'On request',
    images: clusters.map((c) => c.image),
  },
  {
    id: 'study',
    index: 'D2',
    chapter: 'Optional · Study design',
    onScreen: 'Methodology and limitations',
    detail:
      '2,355 DME eyes / 1,688 patients · retrospective, cross-sectional, real-world · OCT biomarker analysis · IRF, SRF, I-HRF, ELM, EZ · automated quantification + model-based clustering · four morphological clusters. Limitations: cross-sectional and exploratory; treatment history not analysed as a selection framework; clusters not validated treatment-selection categories; longitudinal validation required.',
    hcp: 'Interrogates the method.',
    repSays: '“Have you seen this study before?”',
    purpose: 'Available from every scientific page, not pushed.',
    seconds: 'On request',
  },
]
